import { useRef, useState } from "react";
import { ImagePlus, Loader2, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import GenerateResult from "@/components/GenerateResult";
import MaterialsPanel from "@/components/MaterialsPanel";
import { generateDesign } from "@/lib/api";
import { trackEvent, trackConversion, setUserProperty } from "@/lib/analytics";

const STYLES = [
  { id: "modern-minimalist", label: "Modern Minimalist", hint: "Bersih, netral, hangat" },
  { id: "japandi", label: "Japandi", hint: "Zen Jepang × Skandinavia" },
  { id: "scandinavian", label: "Scandinavian", hint: "Cerah, kayu terang, hygge" },
  { id: "industrial", label: "Industrial", hint: "Bata, baja, urban" },
  { id: "bathroom-classic-modern", label: "Bathroom Klasik Modern", hint: "Marble, chrome, glass" },
];

const ROOM_TYPES = ["Ruang Tamu", "Kamar Tidur", "Dapur", "Ruang Makan", "Kamar Mandi", "Ruang Kerja"];

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function Upload() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [style, setStyle] = useState(STYLES[0].id);
  const [roomType, setRoomType] = useState(ROOM_TYPES[0]);
  const [notes, setNotes] = useState("");
  const [luas, setLuas] = useState("");
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Ukuran gambar maks 10MB");
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setGenerated(null);
    trackEvent("photo_uploaded", { size_kb: Math.round(f.size / 1024), type: f.type });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const reset = () => {
    setFile(null);
    setPreviewUrl(null);
    setGenerated(null);
    setNotes("");
  };

  const onGenerate = async () => {
    if (!file) {
      toast.error("Silakan unggah foto ruangan terlebih dahulu");
      return;
    }
    trackEvent("generate_design_click", { style, room_type: roomType, has_notes: notes.length > 0 });
    setLoading(true);
    setGenerated(null);
    try {
      const b64 = await fileToBase64(file);
      const res = await generateDesign({ image_base64: b64, style, room_type: roomType, notes });
      setGenerated(`data:image/png;base64,${res.generated_image_base64}`);
      toast.success("Desain berhasil dibuat!");
      trackConversion("generate_design_success", { style, room_type: roomType });
      setTimeout(() => {
        document.getElementById("hasil-desain")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (e) {
      let msg;
      if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
        msg = "AI butuh waktu lebih lama dari biasanya. Coba ulangi atau sederhanakan catatan Anda.";
      } else if (e?.response?.status === 504) {
        msg = "AI server timeout. Silakan coba lagi atau kosongkan kolom catatan.";
      } else if (e?.response?.status === 502 || e?.response?.status === 503) {
        msg = "AI sementara sibuk. Mohon coba lagi dalam beberapa detik.";
      } else {
        msg = e?.response?.data?.detail || "Gagal generate desain. Coba lagi.";
      }
      toast.error(msg, { duration: 6000 });
      trackEvent("generate_design_error", { status: e?.response?.status || 0, code: e?.code || "unknown" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="upload" data-testid="upload-section" className="relative py-24 lg:py-32 px-6 lg:px-12 bg-gradient-to-b from-white to-sky-50/40">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4 font-semibold">Generate Desain AI</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-tight text-slate-900">
            Unggah ruangan, <span className="italic text-sky-600">lihat hasilnya.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Upload zone */}
          <div className="lg:col-span-7">
            <div
              data-testid="upload-dropzone"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`relative aspect-[4/3] rounded-3xl border-2 border-dashed cursor-pointer overflow-hidden transition-all duration-300 ${
                dragOver ? "border-sky-500 bg-sky-50" : "border-sky-200 bg-white hover:border-sky-400 hover:bg-sky-50/50"
              }`}
            >
              {previewUrl ? (
                <img src={previewUrl} alt="preview" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-sky-100 to-cyan-50 flex items-center justify-center mb-6 shadow-inner">
                    <ImagePlus size={32} className="text-sky-600" strokeWidth={1.5} />
                  </div>
                  <p className="font-display text-2xl text-slate-900 mb-2">Letakkan foto ruangan di sini</p>
                  <p className="text-sm text-slate-500">atau klik untuk memilih file (JPG / PNG, maks 10MB)</p>
                </div>
              )}
              {previewUrl && (
                <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center bg-white/90 backdrop-blur-md px-4 py-3 rounded-xl border border-sky-100">
                  <span className="text-xs text-slate-700 truncate">{file?.name}</span>
                  <button
                    data-testid="upload-reset"
                    onClick={(e) => { e.stopPropagation(); reset(); }}
                    className="text-xs uppercase tracking-[0.15em] text-sky-600 hover:text-sky-700 flex items-center gap-2"
                  >
                    <RotateCcw size={14} /> Ganti
                  </button>
                </div>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="upload-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>

          {/* Controls */}
          <div className="lg:col-span-5 space-y-8">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4 block font-semibold">Jenis Ruangan</label>
              <div className="flex flex-wrap gap-2">
                {ROOM_TYPES.map((r) => (
                  <button
                    key={r}
                    data-testid={`room-type-${r.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => {
                      setRoomType(r);
                      trackEvent("select_room_type", { room_type: r });
                      setUserProperty("preferred_room_type", r);
                    }}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.15em] rounded-full border transition-all ${
                      roomType === r
                        ? "bg-sky-500 text-white border-sky-500 shadow-md shadow-sky-200"
                        : "bg-white border-slate-200 text-slate-700 hover:border-sky-300"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4 block font-semibold">Pilih Gaya Desain</label>
              <div className="space-y-2">
                {STYLES.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`style-${s.id}`}
                    onClick={() => {
                      setStyle(s.id);
                      trackEvent("select_style", { style: s.id, label: s.label });
                      setUserProperty("preferred_style", s.id);
                    }}
                    className={`w-full text-left px-5 py-4 rounded-xl border transition-all flex items-center justify-between ${
                      style === s.id
                        ? "border-sky-500 bg-sky-50"
                        : "border-slate-200 bg-white hover:border-sky-300"
                    }`}
                  >
                    <div>
                      <div className="font-display text-lg text-slate-900">{s.label}</div>
                      <div className="text-xs text-slate-500 mt-1">{s.hint}</div>
                    </div>
                    <div
                      className={`w-4 h-4 rounded-full border-2 ${
                        style === s.id ? "bg-sky-500 border-sky-500" : "border-slate-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3 block font-semibold">
                Luas Ruangan (m²) <span className="text-slate-400 normal-case tracking-normal">— opsional, membantu rekomendasi material</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={luas}
                onChange={(e) => setLuas(e.target.value.replace(/[^\d.]/g, "").slice(0, 6))}
                placeholder="ex: 36"
                data-testid="upload-luas"
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3 block font-semibold">
                Catatan (opsional) <span className="text-slate-400 normal-case tracking-normal">— maks 300 karakter</span>
              </label>
              <textarea
                data-testid="upload-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 300))}
                rows={3}
                maxLength={300}
                placeholder="Contoh: tambahkan tanaman, warna hangat, sofa krem..."
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition resize-none"
              />
              <div className="text-[11px] text-slate-400 mt-1 text-right">{notes.length}/300</div>
            </div>

            <button
              data-testid="generate-design-button"
              onClick={onGenerate}
              disabled={loading || !file}
              className="w-full inline-flex items-center justify-center gap-3 bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 disabled:from-slate-300 disabled:to-slate-300 disabled:shadow-none text-white font-semibold text-sm uppercase tracking-[0.2em] py-5 rounded-full shadow-xl shadow-sky-200 transition-all"
            >
              {loading ? (
                <><Loader2 className="animate-spin" size={18} /> Sedang men-generate…</>
              ) : (
                <><Sparkles size={18} /> Generate Desain dengan AI</>
              )}
            </button>
          </div>
        </div>

        {(loading || generated) && (
          <div id="hasil-desain" className="mt-20 fade-up">
            <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4 font-semibold">Hasil Desain AI</div>
            <h3 className="font-display text-3xl sm:text-4xl font-light tracking-tighter mb-8 text-slate-900">
              Sebelum & Sesudah
            </h3>
            <GenerateResult loading={loading} generated={generated} previewUrl={previewUrl} />
            {generated && (
              <MaterialsPanel style={style} roomType={roomType} luasRuangan={luas} imageBase64={generated} />
            )}
          </div>
        )}
      </div>
    </section>
  );
}
